import React, { useMemo, useState } from "react";

const DataTable = ({
  columns = [],
  data = [],
  entriesPerPage = 10,
  emptyMessage = "No records found.",
  loading = false,
}) => {
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(entriesPerPage);

  // Search
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [data, search]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    return [...filteredData].sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const currentData = sortedData.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handleSort = (accessor) => {
    if (sortColumn === accessor) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(accessor);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (accessor) => {
    if (sortColumn !== accessor)
      return <i className="bi bi-arrow-down-up text-muted small ms-1"></i>;
    return sortDirection === "asc" ? (
      <i className="bi bi-caret-up-fill text-primary small ms-1"></i>
    ) : (
      <i className="bi bi-caret-down-fill text-primary small ms-1"></i>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      {/* Controls Card */}
      <div
        className="card border-0 shadow-sm mb-4"
        style={{ backgroundColor: "white" }}
      >
        <div className="card-body py-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <label className="text-muted small me-3 mb-0">Show</label>
                <select
                  className="form-select form-select-sm me-3"
                  style={{ width: 70 }}
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span className="text-muted small">entries</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center justify-content-md-end mt-3 mt-md-0">
                <label className="text-muted small me-3 mb-0">Search:</label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-3 border-2 ps-4"
                    style={{ width: "250px", borderColor: "#E1E5E9" }}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search..."
                  />
                  <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className="card border-0 shadow-sm mb-4"
        style={{ backgroundColor: "white" }}
      >
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead style={{ backgroundColor: "#F8F9FA" }}>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.accessor}
                      className="px-4 py-3"
                      style={{
                        cursor: col.sortable ? "pointer" : "default",
                        borderBottom: "2px solid #E9ECEF",
                      }}
                      onClick={() => col.sortable && handleSort(col.accessor)}
                    >
                      <div className="d-flex align-items-center">
                        <span className="fw-semibold text-dark">
                          {col.label}
                        </span>
                        {col.sortable && getSortIcon(col.accessor)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      ></div>
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-5 text-muted"
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  currentData.map((row, rowIndex) => (
                    <tr key={row._id || rowIndex} className="border-bottom">
                      {columns.map((col) => (
                        <td
                          key={col.accessor}
                          className="px-4 py-3 align-middle"
                        >
                          {col.renderCell
                            ? col.renderCell(row)
                            : row[col.accessor] || "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div
        className="card border-0 shadow-sm"
        style={{ backgroundColor: "white" }}
      >
        <div className="card-body py-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <div className="text-muted small mb-3 mb-md-0">
              Showing{" "}
              <span className="fw-semibold text-dark">
                {sortedData.length === 0 ? 0 : startIndex + 1}
              </span>{" "}
              to{" "}
              <span className="fw-semibold text-dark">
                {Math.min(endIndex, sortedData.length)}
              </span>{" "}
              of{" "}
              <span className="fw-semibold text-dark">{sortedData.length}</span>{" "}
              entries
            </div>
            <nav aria-label="Page navigation">
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link border-0 rounded-3 me-1 px-3 py-2"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                    style={{
                      backgroundColor: currentPage === 1 ? "#F8F9FA" : "white",
                      color: "#6C757D",
                    }}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link border-0 rounded-3 me-1 px-3 py-2"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    style={{
                      backgroundColor: currentPage === 1 ? "#F8F9FA" : "white",
                      color: "#6C757D",
                    }}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {/* Dynamic page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <li key={pageNum} className="page-item">
                      <button
                        className="page-link border-0 rounded-3 me-1 px-3 py-2 fw-semibold"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          backgroundColor:
                            currentPage === pageNum ? "#0D6EFD" : "white",
                          color: currentPage === pageNum ? "white" : "#6C757D",
                        }}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}

                <li
                  className={`page-item ${
                    currentPage === totalPages || totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >
                  <button
                    className="page-link border-0 rounded-3 me-1 px-3 py-2"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Next page"
                    style={{
                      backgroundColor:
                        currentPage === totalPages || totalPages === 0
                          ? "#F8F9FA"
                          : "white",
                      color: "#6C757D",
                    }}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages || totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >
                  <button
                    className="page-link border-0 rounded-3 px-3 py-2"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label="Last page"
                    style={{
                      backgroundColor:
                        currentPage === totalPages || totalPages === 0
                          ? "#F8F9FA"
                          : "white",
                      color: "#6C757D",
                    }}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataTable;
